import type { Article, CarouselSlide, ChatbotFlow, Doctor, GalleryItem, InsuranceProvider, LandingSection, MedicalSpecialty, NavigationConfig, Service, ServiceCategory, SiteSettings } from '@/types/models'

export const placeholder = '/placeholders/medical-placeholder.svg'

export const siteSettings: SiteSettings = {
  businessName: 'CENDOSCAR',
  descriptiveName: 'Centro de Endoscopia Digestiva y Cardiología',
  tagline: 'Diagnósticos precisos y atención humana.',
  phone: '809-591-6064',
  whatsapp: '18095916064',
  email: 'infocendoscar@gmail.com',
  locationName: 'CENDOSCAR - Centro de Endoscopia Digestiva y Cardiología',
  address: 'Calle Pompeya No. 1, Urb. Italia, Santo Domingo Este',
  city: 'Santo Domingo Este',
  province: 'Santo Domingo',
  country: 'República Dominicana',
  latitude: null,
  longitude: null,
  scheduleText: 'Lunes a viernes: 8:00 a. m. - 5:00 p. m.\nSábados: 8:00 a. m. - 12:00 p. m.\nDomingos: cerrado\nConfirme disponibilidad con recepción.',
  instagramUrl: 'https://www.instagram.com/cendoscarrd/',
  facebookUrl: 'https://web.facebook.com/profile.php?id=61555656447623',
  mapsEmbedUrl: '',
  mapsShareUrl: 'https://www.google.com/maps/search/?api=1&query=Calle+Pompeya+No.+1+Urb.+Italia+Santo+Domingo+Este',
  googleDirectionsUrl: 'https://www.google.com/maps/dir/?api=1&destination=Calle+Pompeya+No.+1+Urb.+Italia+Santo+Domingo+Este&travelmode=driving',
  wazeUrl: 'https://waze.com/ul?q=Calle%20Pompeya%20No.%201%2C%20Urb.%20Italia%2C%20Santo%20Domingo%20Este&navigate=yes',
  locationShareText: 'Visítanos en CENDOSCAR, Calle Pompeya No. 1, Urb. Italia, Santo Domingo Este.',
  logoUrl: '/logo/cendoscar-logo.svg',
  seoTitle: 'CENDOSCAR | Centro diagnóstico en Santo Domingo Este',
  seoDescription: 'Endoscopia digestiva, cardiología, sonografía, Doppler y consultas especializadas.',
  defaultSocialImage: '/social/cendoscar-default-share-1200x630.jpg',
  socialFeed: {
    enabled: true, title: 'Últimas novedades', subtitle: 'Síguenos y conoce nuestras actividades, servicios y recomendaciones.',
    sources: ['instagram', 'facebook'], layout: 'grid', maxItems: 6, showCaption: true, showDate: true,
    showNetworkBadge: true, showAccountName: false, hideDuplicates: true, autoplay: false,
    autoplayDelayMs: 5000, openInNewTab: true, sectionOrder: 11,
  },
}

export const navigationConfig: NavigationConfig = {
  items: [
    { id: 'services', label: 'Servicios', link: { type: 'internalRoute', targetKey: 'services' }, active: true, sortOrder: 1, style: 'normal' },
    { id: 'doctors', label: 'Especialidades médicas', link: { type: 'internalRoute', targetKey: 'doctors' }, active: true, sortOrder: 2, style: 'normal' },
    { id: 'articles', label: 'Artículos', link: { type: 'internalRoute', targetKey: 'articles' }, active: true, sortOrder: 3, style: 'normal' },
    { id: 'contact', label: 'Contacto', link: { type: 'internalRoute', targetKey: 'contact' }, active: true, sortOrder: 4, style: 'normal' },
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      link: { type: 'whatsapp', targetKey: '', whatsappMessage: 'Saludos, deseo información para agendar una cita en CENDOSCAR.' },
      active: true,
      sortOrder: 5,
      style: 'button',
    },
    { id: 'appointment', label: 'Agendar cita', link: { type: 'appointmentForm', targetKey: 'appointment' }, active: true, sortOrder: 6, style: 'highlight' },
  ],
}

if (!navigationConfig.items.some((item) => item.id === 'doctor-interest')) {
  navigationConfig.items.push({
    id: 'doctor-interest',
    label: 'Soy doctor',
    link: { type: 'doctorInterestForm', targetKey: 'doctorInterest' },
    active: true,
    sortOrder: 4.5,
    style: 'normal',
  })
}

const categoryRows = [
  ['gastro', 'Gastroenterología y Endoscopia Digestiva', 'gastroenterologia-endoscopia', 'Prevención, diagnóstico y tratamiento del aparato digestivo.', 'Stethoscope'],
  ['cardio', 'Cardiología', 'cardiologia', 'Evaluación y monitoreo de la salud cardiovascular.', 'HeartPulse'],
  ['sono', 'Sonografía', 'sonografia', 'Estudios de imágenes diagnósticas no invasivas.', 'ScanLine'],
  ['doppler', 'Doppler', 'doppler', 'Evaluación segura del flujo sanguíneo.', 'Activity'],
  ['dolor', 'Manejo del Dolor y Cuidados Paliativos', 'manejo-dolor', 'Atención integral y acompañamiento para mejorar la calidad de vida.', 'HandHeart'],
  ['otros', 'Otros servicios clínicos', 'otros-servicios', 'Consultas, pruebas e intervenciones complementarias.', 'BriefcaseMedical'],
] as const

export const serviceCategories: ServiceCategory[] = categoryRows.map((row, index) => ({
  id: row[0], name: row[1], slug: row[2], description: row[3], iconName: row[4], sortOrder: index + 1, active: true,
}))

const specialtyRows = [
  ['cardiologia', 'Cardiología', 'Evaluación cardiovascular y seguimiento especializado.'],
  ['urologia', 'Urología', 'Atención del sistema urinario y salud urológica.'],
  ['gastroenterologia', 'Gastroenterología', 'Salud digestiva, endoscopía y procedimientos especializados.'],
  ['pediatria', 'Pediatría', 'Cuidado médico integral para niños y adolescentes.'],
  ['endocrinologia', 'Endocrinología', 'Evaluación hormonal, metabólica y diabetes.'],
  ['neumologia-pediatrica', 'Neumología Pediátrica', 'Atención respiratoria especializada para pacientes pediátricos.'],
  ['nutricion', 'Nutrición', 'Orientación nutricional clínica y preventiva.'],
  ['coloproctologia', 'Coloproctología', 'Atención especializada del colon, recto y ano.'],
  ['ginecologia', 'Ginecología', 'Salud femenina y evaluación ginecológica.'],
  ['oncologia-manejo-dolor', 'Oncología / Manejo del Dolor', 'Acompañamiento especializado y manejo del dolor.'],
  ['psicologia', 'Psicología', 'Apoyo emocional y bienestar mental.'],
  ['anestesiologia', 'Anestesiología', 'Seguridad y soporte anestésico en procedimientos.'],
] as const

const specialtyAnimationFiles: Record<string, string> = {
  cardiologia: 'cardiologia.html',
  urologia: 'urologia.html',
  gastroenterologia: 'gastroenterologia.html',
  pediatria: 'pediatria.html',
  endocrinologia: 'endocrinologia.html',
  'neumologia-pediatrica': 'neumologia_pediatrica.html',
  nutricion: 'nutricion.html',
  coloproctologia: 'coloproctologia.html',
  ginecologia: 'ginecologia.html',
  'oncologia-manejo-dolor': 'oncologia_manejo_dolor.html',
  psicologia: 'psicologia.html',
  anestesiologia: 'anestesiologia.html',
}

export const specialties: MedicalSpecialty[] = specialtyRows.map(([id, name, shortDescription], index) => ({
  id,
  slug: id,
  name,
  shortDescription,
  longDescription: `<p>${shortDescription} Esta sección puede ampliarse desde el CMS con contenido educativo e institucional.</p>`,
  imageUrl: '',
  iconName: 'Stethoscope',
  visualType: specialtyAnimationFiles[id] ? 'html5' : 'icon',
  primaryVisual: specialtyAnimationFiles[id] ? 'html5' : 'icon',
  animationHtmlUrl: specialtyAnimationFiles[id] ? `/specialties-html5/${specialtyAnimationFiles[id]}` : '',
  animationEnabled: Boolean(specialtyAnimationFiles[id]),
  gameLinkEnabled: Boolean(specialtyAnimationFiles[id]),
  gameLinkUrl: specialtyAnimationFiles[id] ? `/specialties-html5/${specialtyAnimationFiles[id]}` : '',
  gameLinkOpenMode: 'modal',
  gameLinkTooltip: specialtyAnimationFiles[id] ? `Ver animacion de ${name}` : '',
  active: true,
  sortOrder: index + 1,
}))

const serviceRows: Array<[string, string, string, string, Service['attentionType']]> = [
  ['gastro', 'Gastroscopía', 'gastroscopia', 'Visualización del esófago, estómago y duodeno.', 'procedimiento'],
  ['gastro', 'Colonoscopía', 'colonoscopia', 'Evaluación del colon y recto para detectar alteraciones.', 'procedimiento'],
  ['gastro', 'Colocación de balón gástrico', 'balon-gastrico', 'Apoyo mínimamente invasivo para pérdida de peso bajo supervisión.', 'procedimiento'],
  ['gastro', 'Gastrostomía percutánea endoscópica', 'gastrostomia-peg', 'Soporte para alimentación enteral prolongada.', 'procedimiento'],
  ['gastro', 'Rectosigmoidoscopía', 'rectosigmoidoscopia', 'Evaluación del recto y la parte final del colon.', 'procedimiento'],
  ['gastro', 'Extracción de pólipos', 'polipectomia', 'Retiro preventivo de pólipos durante estudios endoscópicos.', 'procedimiento'],
  ['gastro', 'Test del aliento', 'test-aliento', 'Prueba diagnóstica digestiva.', 'estudio'],
  ['cardio', 'Electrocardiograma (EKG)', 'electrocardiograma', 'Registro de la actividad eléctrica del corazón.', 'estudio'],
  ['cardio', 'Holter', 'holter', 'Monitoreo del ritmo cardíaco durante 24 horas.', 'estudio'],
  ['cardio', 'MAPA', 'mapa', 'Monitoreo ambulatorio de presión arterial.', 'estudio'],
  ['cardio', 'Ecocardiograma', 'ecocardiograma', 'Ultrasonido para evaluar estructura y función cardíaca.', 'estudio'],
  ['sono', 'Sonografía de mama', 'sonografia-mama', 'Estudio de imágenes de tejido mamario.', 'estudio'],
  ['sono', 'Sonografía abdominal', 'sonografia-abdominal', 'Evaluación no invasiva de órganos abdominales.', 'estudio'],
  ['sono', 'Sonografía pélvica', 'sonografia-pelvica', 'Evaluación de órganos y estructuras pélvicas.', 'estudio'],
  ['sono', 'Sonografía obstétrica', 'sonografia-obstetrica', 'Seguimiento por imágenes durante el embarazo.', 'estudio'],
  ['doppler', 'Doppler de miembros inferiores', 'doppler-miembros-inferiores', 'Evaluación del flujo sanguíneo en miembros inferiores.', 'estudio'],
  ['doppler', 'Doppler carotídeo', 'doppler-carotideo', 'Evaluación vascular de los vasos del cuello.', 'estudio'],
  ['dolor', 'Consulta de manejo del dolor', 'consulta-manejo-dolor', 'Evaluación integral del dolor persistente.', 'consulta'],
  ['otros', 'Espirometría', 'espirometria', 'Evaluación funcional respiratoria.', 'estudio'],
  ['otros', 'Papanicolaou', 'papanicolaou', 'Estudio preventivo ginecológico.', 'estudio'],
]

export const services: Service[] = serviceRows.map((row, index) => {
  const category = serviceCategories.find((item) => item.id === row[0])!
  return {
    id: `service-${index + 1}`, categoryId: row[0], categoryName: category.name, name: row[1], slug: row[2],
    shortDescription: row[3], longDescription: `${row[3]} La indicación y preparación deben confirmarse con el equipo médico.`,
    iconName: category.iconName, imageUrl: placeholder, attentionType: row[4] || 'estudio', featured: index < 6, active: true, sortOrder: index + 1,
  }
})

const doctorGroups: Record<string, string[]> = {
  Gastroenterología: ['Dra. Bartolina Romero', 'Dra. Johanna Santana', 'Dra. Eda Gómez', 'Dra. Mariel Rojas', 'Dra. Paty Vólquez', 'Dra. Nilma Japa'],
  Cardiología: ['Dra. Yuleisy de la Rosa', 'Dra. Wilnelia Acosta'],
  Anestesiología: ['Dr. Alexander Estévez', 'Dra. Wendy Maldonado', 'Dra. Rircy Yudelis González', 'Dra. Brígida Gómez'],
  Nutrición: ['Dra. Olga Zapata', 'Dra. Iris Vittini', 'Dra. Ana Sosa', 'Dra. Daviana Almonte'],
  Urología: ['Dr. José García'], Endocrinología: ['Dra. Lorena Padilla'],
  'Oncología / Manejo del Dolor': ['Dra. Yissel Valentin'], Ginecología: ['Dra. Wendy Alcántara'],
  Pediatría: ['Dra. Cherisaida Ureña'], Coloproctología: ['Dra. Wandalis Vargas'],
  'Neumología Pediátrica': ['Dra. Noemí Pérez'], 'Psicología General': ['Lic. Yolanda Torres'],
}

const slugify = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
export const doctors: Doctor[] = Object.entries(doctorGroups).flatMap(([specialty, names]) =>
  names.map((name, index) => ({
    id: `doctor-${slugify(name)}`, name, slug: slugify(name), specialtyId: slugify(specialty), specialtyName: specialty,
    serviceIds: [], photoUrl: placeholder, bio: '', scheduleText: 'Horario por confirmar con recepción',
    exequatur: '', subSpecialty: '',
    instagramUrl: '', facebookUrl: '', linkedinUrl: '', websiteUrl: '',
    featured: index < 2 && ['Gastroenterología', 'Cardiología'].includes(specialty), active: true, sortOrder: index + 1,
  })),
)

doctors.push({
  id: 'doctor-luis-vladimir-restituyo-medrano',
  name: 'Dr. Luis Vladimir Restituyo Medrano',
  slug: 'dr-luis-vladimir-restituyo-medrano',
  specialtyId: 'cirugia-bariatrica-laparoscopica',
  specialtyName: 'Cirugía Bariátrica y Laparoscópica de Alta Complejidad',
  specialtyIds: ['cirugia-bariatrica-laparoscopica'],
  serviceIds: [
    'manga-gastrica',
    'bypass-gastrico',
    'switch-duodenal',
    'bagua',
    'cirugias-revision-postbariatrica',
    'cirugia-vias-biliares',
    'hernia-hiatal',
    'hernia-inguinal',
    'hernia-ventral',
    'reparacion-pared-abdominal',
    'cirugia-antirreflujo-erge',
    'cirugia-metabolica',
    'simpatectomia-laparoscopica',
    'cirugia-colorectal-laparoscopica',
  ],
  photoUrl: '/media/approved/doctors/dr-luis-vladimir-restituyo-medrano-main.png',
  bio: '<p>El Dr. Luis Vladimir Restituyo Medrano es uno de los cirujanos dominicanos más reconocidos en las áreas de cirugía bariátrica y cirugía laparoscópica de alta complejidad. Su trayectoria combina experiencia clínica, formación académica y liderazgo institucional.</p><p>Desde 2010 ha realizado más de 5,000 procedimientos laparoscópicos avanzados, abarcando tanto cirugía bariátrica como cirugía general.</p>',
  scheduleText: 'Horario por confirmar con recepción',
  exequatur: '',
  subSpecialty: 'Cirugía bariátrica, metabólica y laparoscópica avanzada',
  email: 'lvrestituyo@gmail.com',
  instagramUrl: 'https://www.instagram.com/gastrosurgrd/',
  facebookUrl: '',
  linkedinUrl: '',
  websiteUrl: '',
  calendarColor: '#41bec5',
  calendarInviteEnabled: true,
  appointmentDurationMinutes: 45,
  featured: true,
  active: true,
  sortOrder: 1,
})

const articleRows = [
  ['colonoscopia-prevencion', 'Colonoscopía: cuándo realizarla y por qué es importante', 'Gastroenterología', 'Una guía inicial sobre la utilidad preventiva de este estudio.'],
  ['evaluacion-cardiovascular-preventiva', 'Evaluación cardiovascular preventiva', 'Cardiología', 'Conozca por qué revisar su salud cardiovascular de forma oportuna.'],
  ['sonografia-estudios-preparacion', 'Sonografía: estudios comunes y preparación básica', 'Sonografía', 'Recomendaciones generales antes de confirmar su estudio.'],
  ['endoscopia-que-esperar', 'Endoscopia digestiva: qué esperar antes del procedimiento', 'Gastroenterología', 'Información general para conversar con su profesional de salud.'],
  ['manejo-dolor-integral', 'Manejo del dolor: acompañamiento integral', 'Manejo del Dolor', 'El alivio de síntomas requiere una mirada humana y multidisciplinaria.'],
] as const

export const articles: Article[] = articleRows.map((row, index) => ({
  id: `article-${index + 1}`, slug: row[0], title: row[1], categoryId: slugify(row[2]), categoryName: row[2], summary: row[3],
  content: `<p>${row[3]}</p><p>Cada paciente requiere una evaluación individual. Consulte al equipo de CENDOSCAR para recibir orientación apropiada.</p>`,
  authorName: 'Equipo CENDOSCAR', doctorId: '', featuredImageUrl: placeholder, status: index < 3 ? 'published' : 'draft',
  seoTitle: row[1], seoDescription: row[3],
}))

export const galleryItems: GalleryItem[] = ['Fachada', 'Recepción', 'Sala de espera', 'Área de procedimientos'].map((title, index) => ({
  id: `gallery-${index + 1}`, title, description: 'Fotografía real pendiente de carga autorizada.', imageUrl: placeholder,
  alt: `${title} de CENDOSCAR`, type: slugify(title), sectionKey: 'home-gallery', sortOrder: index + 1, active: true,
}))

export const insuranceProviders: InsuranceProvider[] = [
  { id: 'ars-humano', name: 'ARS Humano', slug: 'ars-humano', logoUrl: '', websiteUrl: '', active: true, sortOrder: 1 },
  { id: 'senasa', name: 'SENASA', slug: 'senasa', logoUrl: '', websiteUrl: '', active: true, sortOrder: 2 },
  { id: 'mapfre-salud-ars', name: 'MAPFRE Salud ARS', slug: 'mapfre-salud-ars', logoUrl: '', websiteUrl: '', active: true, sortOrder: 3 },
  { id: 'ars-universal', name: 'ARS Universal', slug: 'ars-universal', logoUrl: '', websiteUrl: '', active: true, sortOrder: 4 },
  { id: 'ars-palic', name: 'ARS Palic', slug: 'ars-palic', logoUrl: '', websiteUrl: '', active: true, sortOrder: 5 },
  { id: 'ars-monumental', name: 'ARS Monumental', slug: 'ars-monumental', logoUrl: '', websiteUrl: '', active: true, sortOrder: 6 },
  { id: 'ars-futuro', name: 'ARS Futuro', slug: 'ars-futuro', logoUrl: '', websiteUrl: '', active: true, sortOrder: 7 },
  { id: 'ars-renacer', name: 'ARS Renacer', slug: 'ars-renacer', logoUrl: '', websiteUrl: '', active: true, sortOrder: 8 },
  { id: 'ars-reservas', name: 'ARS Reservas', slug: 'ars-reservas', logoUrl: '', websiteUrl: '', active: true, sortOrder: 9 },
  { id: 'ars-yunen', name: 'ARS Yunen', slug: 'ars-yunen', logoUrl: '', websiteUrl: '', active: true, sortOrder: 10 },
]

export const carouselSlides: CarouselSlide[] = [
  {
    id: 'carousel-servicios',
    title: 'Servicios',
    subtitle: 'Diagnóstico y atención integral',
    description: 'Consultas especializadas, estudios videoendoscópicos, manometría, sonografías, cápsula endoscópica, Doppler y terapia de Argón Plasma.',
    imageUrl: placeholder,
    imageAlt: 'Servicios médicos CENDOSCAR',
    buttonLabel: 'Más información',
    buttonUrl: '/servicios',
    linkType: 'internal',
    openInNewTab: false,
    intervalSeconds: 6,
    active: true,
    sortOrder: 1,
  },
]

export const landingSections: LandingSection[] = [
  { id: 'carousel', key: 'carousel', eyebrow: 'Servicios destacados', title: 'Carrusel principal', description: 'Modulo de slides administrado desde Carrusel. Desde aqui puede activar, desactivar y ordenar el bloque completo.', enabled: true, sortOrder: 2.5 },
  { id: 'hero', key: 'hero', eyebrow: 'Centro diagnóstico en Santo Domingo Este', title: 'Diagnósticos precisos. Atención humana.', description: 'Servicios digestivos, cardiovasculares y diagnósticos respaldados por un equipo multidisciplinario y trato personalizado.', imageUrl: placeholder, ctaPrimaryLabel: 'Agendar cita', ctaPrimaryUrl: '/agendar-cita', ctaSecondaryLabel: 'Ver servicios', ctaSecondaryUrl: '/servicios', enabled: true, sortOrder: 1 },
  { id: 'trustStrip', key: 'trustStrip', eyebrow: '', title: 'Confianza CENDOSCAR', description: 'Calidad, seguridad y atención personalizada.', enabled: true, sortOrder: 2, metadata: { locationLabel: 'Santo Domingo Este', qualityLabel: 'Calidad y seguridad', careLabel: 'Atención personalizada' } },
  { id: 'servicesIntro', key: 'servicesIntro', eyebrow: 'Servicios', title: 'Cuidado integral en un solo centro', description: 'Conozca nuestras principales unidades diagnósticas y especialidades.', ctaPrimaryLabel: 'Ver todos', ctaPrimaryUrl: '/servicios', enabled: true, sortOrder: 3 },
  { id: 'specialtiesIntro', key: 'specialtiesIntro', eyebrow: 'Especialidades médicas', title: 'Áreas clínicas para una atención integral', description: 'Conozca las principales especialidades disponibles en CENDOSCAR y acceda a sus servicios relacionados.', ctaPrimaryLabel: 'Ver todas las especialidades', ctaPrimaryUrl: '/especialidades', enabled: true, sortOrder: 4 },
  { id: 'proceduresIntro', key: 'proceduresIntro', eyebrow: 'Estudios y procedimientos', title: 'Confirme rápidamente el estudio que necesita', description: 'Encuentre procedimientos diagnósticos, consultas y estudios disponibles para orientar su solicitud de cita.', ctaPrimaryLabel: 'Ver todos los servicios', ctaPrimaryUrl: '/servicios', enabled: true, sortOrder: 5 },
  { id: 'featuredDoctors', key: 'featuredDoctors', eyebrow: 'Especialistas destacados', title: 'Atención médica con especialistas de confianza', description: 'Conozca parte de nuestro equipo médico y confirme disponibilidad al agendar su cita.', ctaPrimaryLabel: 'Ver todos los especialistas', ctaPrimaryUrl: '/especialistas', enabled: true, sortOrder: 6 },
  { id: 'insurancesIntro', key: 'insurancesIntro', eyebrow: 'ARS aceptadas', title: 'Trabajamos con principales aseguradoras', description: 'Consulte las ARS disponibles y confirme la cobertura de su plan antes de su cita.', enabled: true, sortOrder: 7 },
  { id: 'galleryIntro', key: 'galleryIntro', eyebrow: 'Conozca el centro', title: 'Un espacio pensado para su tranquilidad', description: 'Explore nuestras instalaciones. Las fotografías pueden mantenerse desde el administrador.', enabled: true, sortOrder: 8 },
  { id: 'doctorsIntro', key: 'doctorsIntro', eyebrow: 'Especialidades médicas', title: 'Un equipo multidisciplinario', description: 'Directorio institucional, fotografías y horarios mantenidos desde el CMS.', ctaPrimaryLabel: 'Ver especialidades', ctaPrimaryUrl: '/especialistas', enabled: false, sortOrder: 8.5 },
  { id: 'about', key: 'about', eyebrow: 'Quiénes somos', title: 'Calidad, calidez y trato personalizado.', description: 'Somos un centro diagnóstico conformado por profesionales de diferentes especialidades, ofreciendo soluciones de salud con atención centrada en el paciente.', richText: '<p>Trabajamos con responsabilidad, profesionalidad, confianza, empatía y colaboración.</p>', enabled: true, sortOrder: 10 },
  { id: 'missionVision', key: 'missionVision', eyebrow: 'Nuestro propósito', title: 'Misión y visión', description: 'Ofrecer servicios de salud integral y ser un referente diagnóstico de la Zona Oriental.', richText: '<h3>Misión</h3><p>Ofrecer servicios de salud integral con calidad, equidad y eficiencia, con personal humano y altamente capacitado.</p><h3>Visión</h3><p>Ser el principal centro diagnóstico de la Zona Oriental, orientado a la prevención, innovación y atención humana.</p>', enabled: true, sortOrder: 11 },
  { id: 'articlesIntro', key: 'articlesIntro', eyebrow: 'Educación para la salud', title: 'Artículos recientes', description: 'Información general preparada para orientar y promover la consulta profesional.', ctaPrimaryLabel: 'Ver artículos', ctaPrimaryUrl: '/articulos', enabled: true, sortOrder: 12 },
  { id: 'appointmentCta', key: 'appointmentCta', eyebrow: 'Citas', title: 'Solicite su cita en pocos pasos', description: 'Recepción confirmará la disponibilidad. No envíe diagnósticos ni información clínica sensible.', enabled: true, sortOrder: 13, metadata: { point1: 'Elija una fecha preferida', point2: 'Reciba confirmación de recepción', point3: 'Datos protegidos' } },
  { id: 'location', key: 'location', eyebrow: 'Ubicación', title: 'Visítenos en Santo Domingo Este', description: 'Consulte el mapa y abra su aplicación de navegación preferida.', enabled: true, sortOrder: 14 },
  { id: 'footer', key: 'footer', eyebrow: '', title: 'CENDOSCAR', description: 'Diagnósticos precisos y atención humana.', enabled: true, sortOrder: 11 },
]

if (!landingSections.some((section) => section.id === 'doctorInterest')) {
  landingSections.push({
    id: 'doctorInterest',
    key: 'doctorInterest',
    eyebrow: 'Alianzas profesionales',
    title: 'Soy doctor / quiero consultorio / quiero ser parte del centro',
    description: 'Complete una solicitud administrativa para explorar integraciÃ³n profesional, consultorio o colaboraciÃ³n con CENDOSCAR.',
    ctaPrimaryLabel: 'Enviar solicitud',
    ctaPrimaryUrl: '/soy-doctor',
    enabled: true,
    sortOrder: 9,
  })
}

export const chatbotFlows: ChatbotFlow[] = [{
  id: 'main',
  key: 'main',
  title: 'Asistente CENDOSCAR',
  initialNodeId: 'welcome',
  enabled: true,
  sortOrder: 1,
  nodes: [
    {
      id: 'welcome',
      message: 'Hola. Puedo ayudarle con citas, servicios, ubicación y formas de contacto.',
      helperText: 'Este asistente responde preguntas generales y ayuda a solicitar citas. No sustituye una evaluación médica. En caso de emergencia, contacte servicios de emergencia.',
      options: [
        { label: 'Agendar una cita', nextNodeId: 'appointment-type' },
        { label: 'Servicios disponibles', nextNodeId: 'services' },
        { label: 'Ubicación y horario', action: 'go_to_location' },
        { label: 'Hablar por WhatsApp', action: 'open_whatsapp' },
        { label: 'Llamar al centro', action: 'call_phone' },
        { label: 'Preparación para procedimientos', nextNodeId: 'preparation' },
      ],
    },
    {
      id: 'appointment-type',
      message: '¿Qué tipo de atención desea solicitar?',
      options: [
        { label: 'Consulta', action: 'go_to_appointment', payload: { attentionType: 'consulta' } },
        { label: 'Estudio', action: 'go_to_appointment', payload: { attentionType: 'estudio' } },
        { label: 'Procedimiento', action: 'go_to_appointment', payload: { attentionType: 'procedimiento' } },
        { label: 'Volver al inicio', action: 'restart' },
      ],
    },
    {
      id: 'services',
      message: 'Ofrecemos Gastroenterología y Endoscopia, Cardiología, Sonografía, Doppler, Manejo del Dolor y otros servicios clínicos.',
      options: [
        { label: 'Ver todos los servicios', action: 'go_to_services' },
        { label: 'Solicitar cita', nextNodeId: 'appointment-type' },
        { label: 'Quiero que me contacten', action: 'capture_lead' },
        { label: 'Volver al inicio', action: 'restart' },
      ],
    },
    {
      id: 'preparation',
      message: 'La preparación puede variar según el procedimiento. Recepción o el profesional de salud le indicará las instrucciones. Puede solicitar su cita para recibir orientación.',
      options: [
        { label: 'Solicitar cita', nextNodeId: 'appointment-type' },
        { label: 'Hablar por WhatsApp', action: 'open_whatsapp' },
        { label: 'Volver al inicio', action: 'restart' },
      ],
    },
  ],
}]
